<script setup lang="ts">
import type { ILegalText } from "~/models/ILegalText";
import type {
  ILinkChain,
  INote,
  IParagraphEntry,
  ISchema,
} from "~/models/IUserEntry";
import type { ILegalTextParagraph } from "~/models/ILegalTextParagraph";
import type { IToC } from "~/models/IToC";
import type { MaybeElementRef } from "@vueuse/core";
import { paragraphInViewObserver } from "~/components/legaltextparagraph/paragraph/fullView/paragraphInViewObserver";
import { setupRowVirtualizer } from "~/components/legaltextparagraph/paragraph/fullView/setupRowVirtualizer";

type Props = {
  flatToc: (IToC | ILegalTextParagraph)[];
  legalText: ILegalText;
  legalTextParagraph: ILegalTextParagraph | undefined;
  toolIsSelected: boolean;
};

type Emits = {
  delete: [entry: IParagraphEntry];
  move: [entry: ILinkChain | INote | ISchema];
  editLink: [entry: ILinkChain];
  selection: [paragraph: ILegalTextParagraph, touch: boolean];
};

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { legalText } = toRefs(props);

const tocOrder = computed(() =>
  props.flatToc
    .filter((t) => "paragraph" in t)
    .findIndex((t) => t.id === props.legalTextParagraph?.id)
);

const {
  status,
  error,
  refetch,
  data: paragraphs,
  fetchPreviousPage,
  hasPreviousPage,
  isFetchingNextPage,
  isFetchingPreviousPage,
  hasNextPage,
  fetchNextPage,
} = usePaginatedParagraphs(legalText, tocOrder);

const parentRef = useTemplateRef<HTMLElement>("parentRef");
const bottomRef = useTemplateRef<HTMLElement>("bottomRef");
const topRef = useTemplateRef<HTMLElement>("topRef");

const { rowVirtualizer, allItems, virtualRows, totalSize } =
  setupRowVirtualizer(() => props.flatToc, paragraphs, parentRef);

const currentElements = ref<Element[]>([]);

const scrollingEnabled = ref(true);
const initialScrollingDone = ref(false);

paragraphInViewObserver(
  currentElements,
  () => legalText.value.slug,
  allItems,
  parentRef,
  logicAnd(scrollingEnabled, initialScrollingDone)
);

onBeforeUpdate(() => {
  currentElements.value = [];
});

const measureElement = (el: any) => {
  nextTick(() => {
    if (el) {
      rowVirtualizer.value.measureElement(el);
      currentElements.value.push(el);
    }
  });
};

const scrollToIndex = (idx: number) => {
  return new Promise<void>((resolve) => {
    scrollingEnabled.value = false;
    setTimeout(() => {
      rowVirtualizer.value.scrollToIndex(idx, {
        align: "start",
      });
      scrollingEnabled.value = true;
      resolve();
    }, 100);
  });
};

const scrollIfNeeded = async (p?: ILegalTextParagraph) => {
  if (!p || !scrollingEnabled.value) {
    return;
  }

  const loaded = paragraphs.value.some(({ id }) => id === p.id);
  if (!loaded) {
    // if the paragraph is not in the current list, fetch the corresponding page
    await refetch();
  }

  await nextTick();

  const idx = allItems.value.findIndex(({ id }) => id === p.id);
  // if the element is not in the virtualRows, scroll to it
  const contained = virtualRows.value.some((r) => r.index === idx);

  if (!contained) {
    await scrollToIndex(idx);
  }
};

onMounted(async () => {
  await scrollIfNeeded(props.legalTextParagraph);
  initialScrollingDone.value = true;
});

watch(
  () => props.legalTextParagraph,
  (p) => {
    return scrollIfNeeded(p);
  }
);

const makeLoaderObserver = (elRef: MaybeElementRef, fn: () => Promise<void>) =>
  useIntersectionObserver(
    elRef,
    ([entry]) => {
      if (
        !initialScrollingDone.value ||
        !scrollingEnabled.value ||
        !entry.isIntersecting
      ) {
        return;
      }
      return fn();
    },
    { root: parentRef, rootMargin: "50px" }
  );

makeLoaderObserver(topRef, async () => {
  if (hasPreviousPage.value && !isFetchingPreviousPage.value) {
    const currentlyFirst = allItems.value[0];
    await fetchPreviousPage();
    await nextTick();
    const index = allItems.value.findIndex(
      (item) =>
        item.id === currentlyFirst.id && item.title === currentlyFirst.title
    );
    await scrollToIndex(index);
  }
});

makeLoaderObserver(bottomRef, async () => {
  if (hasNextPage.value && !isFetchingNextPage.value) {
    await fetchNextPage();
  }
});
</script>

<template>
  <div>
    <div
      v-if="status === 'pending'"
      class="flex h-full items-center justify-center"
    >
      <i-line-md-loading-twotone-loop class="h-16 w-16" />
    </div>
    <p v-else-if="status === 'error'">Error: {{ (error as Error).message }}</p>
    <div
      v-else
      ref="parentRef"
      class="h-full overflow-y-auto contain-strict"
      style="overflow-anchor: none"
    >
      <div class="relative w-full" :style="{ height: `${totalSize}px` }">
        <div
          class="absolute left-0 top-0 w-full"
          :style="{
            transform: `translateY(${virtualRows[0]?.start ?? 0}px)`,
          }"
        >
          <div id="top" ref="topRef" />
          <section
            v-for="virtualRow in virtualRows"
            :key="virtualRow.key as string"
            :ref="measureElement"
            class="py-4"
            :data-index="virtualRow.index"
          >
            <FullViewParagraphOrToc
              :legalText
              :item="allItems[virtualRow.index]"
              :toolIsSelected
              @delete="emit('delete', $event)"
              @move="emit('move', $event)"
              @editLink="emit('editLink', $event)"
              @selection="(...args) => emit('selection', ...args)"
            />
          </section>
          <div id="bottom" ref="bottomRef" />
        </div>
      </div>
    </div>
  </div>
</template>
